/**
 * Background-job state layer.
 *
 * Source of truth is the backend Job table. The provider polls
 * /api/v1/jobs and merges optimistic submissions, so dock + inline
 * badges survive page navigation and browser refresh. Multi-tab
 * coherence via BroadcastChannel.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api } from './api';
import { getStoredUser } from './auth';

export type JobKind =
  | 'summary'
  | 'findings'
  | 'violation'
  | 'timeline'
  | 'entity'
  | 'inconsistency'
  | 'pii_llm'
  | 'reindex';

export type JobStatus = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED' | 'CANCELLED';

export interface Job {
  id: string;
  kind: JobKind;
  doc_id: string | null;
  user_id: string;
  user_email: string;
  status: JobStatus;
  stage: string | null;
  progress: number | null;
  params: Record<string, unknown>;
  result_ref: Record<string, unknown> | null;
  error: string | null;
  celery_task_id: string | null;
  eta_ms: number | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

const ACTIVE: JobStatus[] = ['PENDING', 'RUNNING'];
const TERMINAL: JobStatus[] = ['DONE', 'FAILED', 'CANCELLED'];

function isActive(s: JobStatus) {
  return ACTIVE.includes(s);
}

interface JobsContextValue {
  active: Job[];
  recent: Job[];
  /** All visible jobs (active + recent). */
  all: Job[];
  /** Returns the most recent matching job (active first). */
  find: (predicate: (j: Job) => boolean) => Job | undefined;
  submit: (
    kind: JobKind,
    docId: string | null,
    params?: Record<string, unknown>,
  ) => Promise<Job>;
  cancel: (jobId: string) => Promise<void>;
  retry: (jobId: string) => Promise<Job>;
  refresh: () => Promise<void>;
  /** Fired exactly once per (job_id × terminal status). */
  onTerminal: (cb: (job: Job, prev: JobStatus) => void) => () => void;
}

const Ctx = createContext<JobsContextValue | null>(null);

export function useJobs() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useJobs must be inside <JobsProvider>');
  return v;
}

const POLL_ACTIVE_MS = 3000;
const POLL_IDLE_MS = 30000;
const SINCE_HOURS = 24;

const BCAST_CHANNEL = 'netra-jobs';
type BcastMsg = { type: 'changed'; ts: number };

export function JobsProvider({ children }: { children: React.ReactNode }) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const prevStatusRef = useRef<Map<string, JobStatus>>(new Map());
  const terminalCbsRef = useRef<Set<(j: Job, prev: JobStatus) => void>>(new Set());
  const bcastRef = useRef<BroadcastChannel | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchingRef = useRef(false);

  const fetchJobs = useCallback(async () => {
    if (!getStoredUser()) return; // skip when logged out
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const data = await api<Job[]>(
        `/api/v1/jobs?since_hours=${SINCE_HOURS}&limit=100`,
      );
      setJobs((curr) => {
        // Preserve optimistic jobs that haven't appeared in server response
        // yet (race window between POST and the next poll).
        const serverIds = new Set(data.map((d) => d.id));
        const pendingClient = curr.filter(
          (c) => !serverIds.has(c.id) && Date.now() - new Date(c.created_at).getTime() < 10_000,
        );
        const merged = [...data, ...pendingClient];
        // Fire onTerminal callbacks for status transitions.
        const seen = prevStatusRef.current;
        const next = new Map<string, JobStatus>();
        for (const j of merged) {
          const prev = seen.get(j.id);
          next.set(j.id, j.status);
          if (prev && isActive(prev) && TERMINAL.includes(j.status)) {
            for (const cb of terminalCbsRef.current) {
              try {
                cb(j, prev);
              } catch {
                /* ignore listener errors */
              }
            }
          }
        }
        prevStatusRef.current = next;
        return merged;
      });
    } catch {
      /* swallow — next poll will retry */
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  // Polling loop — interval depends on whether there are active jobs.
  useEffect(() => {
    const tick = async () => {
      await fetchJobs();
      const hasActive = jobs.some((j) => isActive(j.status));
      const delay = hasActive ? POLL_ACTIVE_MS : POLL_IDLE_MS;
      pollTimerRef.current = setTimeout(tick, delay);
    };
    pollTimerRef.current = setTimeout(tick, 100);
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
    // Re-arm cadence when the active set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs.some((j) => isActive(j.status))]);

  // BroadcastChannel — other tabs nudge us to refetch immediately.
  useEffect(() => {
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return;
    const ch = new BroadcastChannel(BCAST_CHANNEL);
    bcastRef.current = ch;
    ch.onmessage = (e: MessageEvent<BcastMsg>) => {
      if (e.data?.type === 'changed') fetchJobs();
    };
    return () => {
      ch.close();
      bcastRef.current = null;
    };
  }, [fetchJobs]);

  // Tab focus → refresh immediately.
  useEffect(() => {
    const onFocus = () => fetchJobs();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchJobs]);

  const broadcast = useCallback(() => {
    bcastRef.current?.postMessage({ type: 'changed', ts: Date.now() } satisfies BcastMsg);
  }, []);

  const submit = useCallback(
    async (
      kind: JobKind,
      docId: string | null,
      params: Record<string, unknown> = {},
    ): Promise<Job> => {
      const job = await api<Job>('/api/v1/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, doc_id: docId, params }),
      });
      // Optimistic insert at the top.
      setJobs((curr) => {
        const existing = curr.findIndex((j) => j.id === job.id);
        if (existing >= 0) {
          const next = curr.slice();
          next[existing] = job;
          return next;
        }
        return [job, ...curr];
      });
      broadcast();
      // Force a fresh poll soon so the badge transitions to RUNNING quickly.
      setTimeout(fetchJobs, 600);
      return job;
    },
    [broadcast, fetchJobs],
  );

  const cancel = useCallback(
    async (jobId: string) => {
      const updated = await api<Job>(`/api/v1/jobs/${jobId}`, { method: 'DELETE' });
      setJobs((curr) => curr.map((j) => (j.id === updated.id ? updated : j)));
      broadcast();
    },
    [broadcast],
  );

  const retry = useCallback(
    async (jobId: string): Promise<Job> => {
      const newJob = await api<Job>(`/api/v1/jobs/${jobId}/retry`, { method: 'POST' });
      setJobs((curr) => [newJob, ...curr]);
      broadcast();
      setTimeout(fetchJobs, 600);
      return newJob;
    },
    [broadcast, fetchJobs],
  );

  const onTerminal = useCallback((cb: (j: Job, prev: JobStatus) => void) => {
    terminalCbsRef.current.add(cb);
    return () => {
      terminalCbsRef.current.delete(cb);
    };
  }, []);

  const value = useMemo<JobsContextValue>(() => {
    const active = jobs.filter((j) => isActive(j.status));
    const recent = jobs.filter((j) => TERMINAL.includes(j.status));
    return {
      active,
      recent,
      all: jobs,
      find: (pred) => active.find(pred) || recent.find(pred),
      submit,
      cancel,
      retry,
      refresh: fetchJobs,
      onTerminal,
    };
  }, [jobs, submit, cancel, retry, fetchJobs, onTerminal]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// ─── Convenience helpers (typed submit shortcuts) ───────────────────────────

export const KIND_LABEL: Record<JobKind, string> = {
  summary: 'Ringkasan',
  findings: 'Temuan',
  violation: 'Pelanggaran',
  timeline: 'Kronologi',
  entity: 'Entitas',
  inconsistency: 'Inkonsistensi',
  pii_llm: 'PII Scan',
  reindex: 'Reindex BAP',
};

export const KIND_VIEW: Record<JobKind, string> = {
  summary: 'summary',
  findings: 'findings',
  violation: 'violation',
  timeline: 'timeline',
  entity: 'entity',
  inconsistency: 'inconsistency',
  pii_llm: 'pii',
  reindex: 'reindex',
};

export function jobElapsedMs(j: Job): number {
  const start = j.started_at ? new Date(j.started_at).getTime() : new Date(j.created_at).getTime();
  const end = j.completed_at ? new Date(j.completed_at).getTime() : Date.now();
  return Math.max(0, end - start);
}

export function jobEtaRemainingMs(j: Job): number | null {
  if (j.status !== 'RUNNING' || j.eta_ms == null) return null;
  const elapsed = jobElapsedMs(j);
  return Math.max(0, j.eta_ms - elapsed);
}

export function fmtDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  if (m < 1) return `${s}s`;
  const rem = s - m * 60;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
}
